/**
 * Докуда консоль дотягивается.
 *
 * Отдельным разделом, а не вкладкой внутри общей картины. Матрица отвечает
 * «что защищено», а этот список — «с чем мы вообще связаны». Объём контроля
 * отличается от объёма покрытия: можно иметь безупречные копии там, куда
 * дотянулись, и не знать о половине хозяйства. Слепые зоны видно именно
 * здесь, и прятать их во вторую вкладку было неправильно.
 */
import { useQuery } from '../transport/hooks.ts';
import { agoPhrase } from '../lib/format.ts';
import type { OverviewSnapshot } from '../../shared/protocol/overview.ts';

const KIND_ICON: Record<string, string> = {
  hypervisor: '▤', share: '▥', database: '▦', cloud: '☁', storage: '▧', mail: '✉',
};

export function Links(): React.JSX.Element {
  const { data, error, loading } = useQuery<OverviewSnapshot>('overview.get');

  if (error) {
    return (
      <section className="panel panel--fill">
        <h2 className="panel__title">Связи</h2>
        <p className="alert alert--error">Ошибка: {error.msg}</p>
      </section>
    );
  }
  if (!data) {
    return (
      <section className="panel panel--fill">
        <h2 className="panel__title">Связи</h2>
        <p className="muted">{loading ? 'Проверяем связи…' : 'Нет данных.'}</p>
      </section>
    );
  }

  const items = data.integrations;
  const live = items.filter((i) => i.enabled && i.reachable === true).length;
  const off = items.filter((i) => !i.configured).length;

  return (
    <section className="panel panel--fill">
      <header className="panel__head">
        <h2 className="panel__title">Связи</h2>
        {items.length === 0 ? (
          <p className="muted">Проверка связей ещё не публиковалась — worker молчит.</p>
        ) : (
          <p className="links__lead">
            Настроено {items.length - off} из {items.length}, отвечают {live}.{' '}
            <span className="muted">
              Проверено {new Date(items[0]!.checkedAt).toLocaleTimeString('ru-RU')}.
            </span>
          </p>
        )}
      </header>

      <div className="panel__scroll">
        <table className="table links__table">
          <thead>
            <tr>
              <th scope="col">Связь</th>
              <th scope="col">Отвечает</th>
              <th scope="col">Сбор</th>
              <th scope="col">Чем представляемся</th>
              <th scope="col">Что даёт</th>
            </tr>
          </thead>
          <tbody>
            {items.map((i) => (
              <tr key={i.key} className={!i.configured ? 'links__row--off' : ''}>
                <th scope="row" className="links__name">
                  <span className="links__icon" aria-hidden="true">{KIND_ICON[i.kind] ?? '•'}</span>
                  <span>
                    <span className="links__label">{i.label}</span>
                    <span className="links__endpoint mono">{i.endpoint}</span>
                    {i.detail && <span className="links__detail">{i.detail}</span>}
                  </span>
                </th>

                {/*
                  Доступность и доступ — разные факты, и колонки у них разные.
                  Открытый порт не значит, что нас пустят; успешный сбор значит,
                  что учётные данные работали. Склеить их в одну галочку —
                  соврать в обе стороны сразу.
                */}
                <td className="links__state">
                  {!i.configured ? <span className="muted">не настроено</span>
                    : !i.enabled ? <span className="muted">выключено</span>
                    : i.reachable === true ? <span className="links__yes">отвечает</span>
                    : i.reachable === false ? <span className="links__no">молчит</span>
                    : <span className="muted">не проверялось</span>}
                </td>

                <td className="links__state">
                  {i.collectorOk === true ? (
                    <span className="links__yes" title={`Последний сбор: ${i.lastCollectAt ?? ''}`}>
                      {agoPhrase(i.lastCollectAt)}
                    </span>
                  ) : i.collectorOk === false ? <span className="links__no">сбой</span>
                    : <span className="muted">—</span>}
                </td>

                <td className="links__access">{i.access}</td>
                <td className="links__provides">{i.provides}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
